import React, { useState } from 'react';
import { Auth } from 'aws-amplify';
import { Button, TextField, Typography, Alert, CircularProgress } from '@mui/material';

/**
 * NewPasswordSetup handles the NEW_PASSWORD_REQUIRED challenge from Cognito.
 * This occurs when a user logs in with a temporary password for the first time.
 * After setting a new password, it transitions to MFA setup.
 */
function NewPasswordSetup({ userSession, onPasswordSet, onError }) {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleSetNewPassword = async (e) => {
    e.preventDefault();
    setErrorMessage('');

    if (newPassword !== confirmPassword) {
      setErrorMessage('Passwords do not match.');
      return;
    }

    if (newPassword.length < 16) {
      setErrorMessage('Password must be at least 16 characters long.');
      return;
    }

    // Password complexity check
    // const hasUppercase = /[A-Z]/.test(newPassword);
    // const hasLowercase = /[a-z]/.test(newPassword);
    // const hasNumber = /[0-9]/.test(newPassword);
    // const hasSpecial = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(newPassword);

    // if (!hasUppercase || !hasLowercase || !hasNumber || !hasSpecial) {
    //   setErrorMessage(
    //     'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character.'
    //   );
    //   return;
    // }

    setLoading(true);

    try {
      const updatedUser = await Auth.completeNewPassword(userSession, newPassword);
      // After setting password, the user object is returned.
      // We pass it up so the parent can proceed to MFA setup.
      onPasswordSet(updatedUser);
    } catch (error) {
      console.error('New password error:', error);
      if (error.code === 'InvalidPasswordException') {
        setErrorMessage(error.message || 'Password does not meet requirements.');
      } else {
        setErrorMessage(error.message || 'Failed to set new password. Please try again.');
      }
      if (onError) onError(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', width: '280px' }}>
      <Typography variant="h6" style={{ textAlign: 'center' }}>
        Set New Password
      </Typography>
      <Typography variant="body2" style={{ textAlign: 'center' }}>
        Your temporary password has expired. Please set a new permanent password.
      </Typography>

      {errorMessage && (
        <Alert severity="error">{errorMessage}</Alert>
      )}

      <form onSubmit={handleSetNewPassword} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
        <TextField
          label="New Password"
          type={showNewPassword ? 'text' : 'password'}
          variant="outlined"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          required
          disabled={loading}
          InputProps={{
            endAdornment: (
              <Button
                onClick={() => setShowNewPassword((show) => !show)}
                size="small"
                tabIndex={-1}
              >
                {showNewPassword ? 'Hide' : 'Show'}
              </Button>
            ),
          }}
        />
        <TextField
          label="Confirm New Password"
          type={showConfirmPassword ? 'text' : 'password'}
          variant="outlined"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          required
          disabled={loading}
          InputProps={{
            endAdornment: (
              <Button
                onClick={() => setShowConfirmPassword((show) => !show)}
                size="small"
                tabIndex={-1}
              >
                {showConfirmPassword ? 'Hide' : 'Show'}
              </Button>
            ),
          }}
        />
        <Button type="submit" color="primary" variant="contained" size="large" disabled={loading}>
          {loading ? <CircularProgress size={24} /> : 'Set Password'}
        </Button>
      </form>
    </div>
  );
}

export default NewPasswordSetup;
